import type { IntegrationKey } from "schemas/agentTools/integrationDefinitions";
import type {
  OpencodeExecuteToolCall,
  OpencodeMessagePart,
  OpencodeToolPart,
  OpencodeToolState,
} from "schemas/generated/agentTools/opencode";
import type { TodoWriteState } from "../display/opencode";
import type { OpencodeToolDisplay } from "../display/shared";
import type { OpencodeToolIconKey } from "../metadata";

import { formatDuration } from "replo-utils/lib/datetime";
import { parseJsonOrNull } from "replo-utils/lib/json";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import {
  opencodeExecuteToolMetadataSchema,
  opencodeQuestionToolInputSchema,
  opencodeToolIconKeyByName,
} from "schemas/agentTools/opencode";
import { mcpShowIntegrationStatusOutputSchema } from "schemas/agentTools/replo/bedrockMcp";
import {
  reploOpencodeHarnessSuggestFollowupsToolInputSchema,
  reploOpencodeHarnessToolIconKeyByName,
} from "schemas/agentTools/reploOpencodeHarness";
import { z } from "zod";

import { getNangoIntegrationToolDisplay } from "../display/integration";
import { getMcpToolDisplay } from "../display/mcp/mcp";
import {
  getMainOpencodeToolDisplay,
  getTodoWriteState,
} from "../display/opencode";
import { getReploOpencodeHarnessToolDisplay } from "../display/ReploOpencodeHarness";
import {
  getToolDisplayTextValue,
  parseRecordOrNull,
  quotedDetail,
  titleCase,
} from "../display/shared";
import {
  getKnownIntegrationDetails,
  getOpencodeToolIconKey,
} from "../metadata";

const persistedQuestionAnswersSchema = z.array(z.array(z.string()));

export type AgentToolQuestion = {
  id?: string;
  prompt?: string;
  question?: string;
  header?: string;
  options: {
    id?: string;
    label: string;
    description?: string;
    imageUrl?: string;
  }[];
  multiple?: boolean;
  allow_multiple?: boolean;
  custom?: boolean;
};

export type AgentToolIntegrationStatusPayload = z.infer<
  typeof mcpShowIntegrationStatusOutputSchema
>;

type AgentToolRecord = OpencodeToolState["input"];
type AgentToolIntegrationRendering = null | {
  key: IntegrationKey;
  name: string;
  logoUrl: string | null;
};

type ResolvedOpencodeToolDisplay = Pick<
  OpencodeToolDisplay,
  "integrationKey"
> & {
  header: string | null;
  summary: string | null;
};

type AgentToolRenderingModel = {
  display: {
    header: string | null;
    summary: string | null;
  };
  integration: AgentToolIntegrationRendering;
};

type KnownOpencodeToolName = keyof typeof opencodeToolIconKeyByName;
type KnownReploOpencodeHarnessToolName =
  keyof typeof reploOpencodeHarnessToolIconKeyByName;

function isKnownOpencodeToolName(
  toolName: string,
): toolName is KnownOpencodeToolName {
  return Object.hasOwn(opencodeToolIconKeyByName, toolName);
}

function isKnownReploOpencodeHarnessToolName(
  toolName: string,
): toolName is KnownReploOpencodeHarnessToolName {
  return Object.hasOwn(reploOpencodeHarnessToolIconKeyByName, toolName);
}

export type AgentToolModel = {
  id: string;
  callId: string;
  /** callID of the originating tool part; exploded code-mode rows share it. */
  partCallId?: string;
  toolName: string;
  display: {
    header: string | null;
    summary: string | null;
  };
  status: OpencodeToolState["status"];
  timing: {
    startedAtMs: number | null;
    endedAtMs: number | null;
    durationMs: number | null;
    formattedDuration: string | null;
  };
  icon: OpencodeToolIconKey;
  integration: AgentToolRenderingModel["integration"];
  debug: {
    input: AgentToolRecord;
    output: string | null;
    error: string | null;
  };
  content:
    | { kind: "none" }
    | { kind: "todoWrite"; state: TodoWriteState }
    | {
        kind: "question";
        questions: AgentToolQuestion[];
        persistedAnswers: string[][] | null;
      }
    | { kind: "subagentTask"; callId: string }
    | { kind: "followUps"; suggestions: string[] }
    | {
        kind: "integrationStatus";
        integrationKey: string | null;
        payload: AgentToolIntegrationStatusPayload | null;
      };
};

function getKnownOpencodeToolNameOrNull({ toolName }: { toolName: string }) {
  return isKnownOpencodeToolName(toolName) ? toolName : null;
}

function getKnownReploOpencodeHarnessToolNameOrNull({
  toolName,
}: {
  toolName: string;
}) {
  return isKnownReploOpencodeHarnessToolName(toolName) ? toolName : null;
}

function getTimingViewModel({ toolPart }: { toolPart: OpencodeToolPart }) {
  const stateTime = "time" in toolPart.state ? toolPart.state.time : null;
  const startedAtMs = stateTime?.start ?? null;
  const endedAtMs =
    stateTime && "end" in stateTime && typeof stateTime.end === "number"
      ? stateTime.end
      : null;
  const durationMs =
    startedAtMs !== null && endedAtMs !== null ? endedAtMs - startedAtMs : null;
  return {
    startedAtMs,
    endedAtMs,
    durationMs,
    formattedDuration: durationMs !== null ? formatDuration(durationMs) : null,
  };
}

function getRawToolDisplay({
  currentSessionToolParts,
  currentToolPart,
}: {
  currentSessionToolParts: readonly OpencodeToolPart[];
  currentToolPart: OpencodeToolPart;
}) {
  const normalizedToolName = currentToolPart.tool.toLowerCase();
  const opencodeToolName = getKnownOpencodeToolNameOrNull({
    toolName: normalizedToolName,
  });
  if (opencodeToolName) {
    return getResolvedOpencodeToolDisplay({
      display: getMainOpencodeToolDisplay({
        toolName: opencodeToolName,
        toolPart: currentToolPart,
        toolParts: currentSessionToolParts,
      }),
      status: currentToolPart.state.status,
    });
  }

  const reploOpencodeHarnessToolName =
    getKnownReploOpencodeHarnessToolNameOrNull({
      toolName: normalizedToolName,
    });
  if (reploOpencodeHarnessToolName) {
    const display = getReploOpencodeHarnessToolDisplay({
      toolName: reploOpencodeHarnessToolName,
      toolPart: currentToolPart,
    });
    return getResolvedOpencodeToolDisplay({
      display,
      status: currentToolPart.state.status,
    });
  }

  const nangoIntegrationToolDisplay = getNangoIntegrationToolDisplay({
    toolPart: currentToolPart,
  });
  if (nangoIntegrationToolDisplay.integrationKey) {
    return getResolvedOpencodeToolDisplay({
      display: nangoIntegrationToolDisplay,
      status: currentToolPart.state.status,
    });
  }

  const mcpToolDisplay = getMcpToolDisplay({
    toolName: normalizedToolName,
    toolPart: currentToolPart,
    toolParts: currentSessionToolParts,
  });
  if (mcpToolDisplay) {
    return getResolvedOpencodeToolDisplay({
      display: mcpToolDisplay,
      status: currentToolPart.state.status,
    });
  }

  return getResolvedOpencodeToolDisplay({
    display: nangoIntegrationToolDisplay,
    status: currentToolPart.state.status,
  });
}

function getResolvedOpencodeToolDisplay({
  display,
  status,
}: {
  display: OpencodeToolDisplay;
  status: OpencodeToolState["status"];
}): ResolvedOpencodeToolDisplay {
  const summary = getToolDisplayTextValue({
    text: display.summary,
    status,
  });
  const title = getToolDisplayTitle({
    display: { detail: display.detail, summary },
    status,
  });
  const header =
    getToolDisplayTextValue({
      text: display.header ?? null,
      status,
    }) ??
    title ??
    summary;

  return {
    integrationKey: display.integrationKey,
    header,
    summary,
  };
}

function getToolDisplayTitle({
  display,
  status,
}: {
  display: { detail: string | null; summary: string | null };
  status: OpencodeToolState["status"];
}) {
  if (!display.summary) {
    return null;
  }
  const title = display.detail
    ? `${display.summary} ${display.detail}`
    : display.summary;
  return status === "error" ? `${title} failed` : title;
}

function getToolIntegrationRendering({
  display,
}: {
  display: ResolvedOpencodeToolDisplay;
}) {
  if (display.integrationKey === undefined) {
    return null;
  }
  const integrationDetails = getKnownIntegrationDetails({
    integrationKey: display.integrationKey,
  });
  return {
    key: display.integrationKey,
    name: integrationDetails?.name ?? display.integrationKey,
    logoUrl: integrationDetails?.logoUrl ?? null,
  };
}

function getQuestionContent({
  currentToolPart,
}: {
  currentToolPart: OpencodeToolPart;
}) {
  const parsedInput = opencodeQuestionToolInputSchema.safeParse(
    currentToolPart.state.input,
  );
  const stateMetadata =
    "metadata" in currentToolPart.state ? currentToolPart.state.metadata : null;
  const persistedAnswers = persistedQuestionAnswersSchema.safeParse(
    parseRecordOrNull(stateMetadata)?.answers,
  );

  const questionContent: AgentToolModel["content"] = {
    kind: "question",
    questions: parsedInput.success
      ? parsedInput.data.questions.map((question) => ({
          ...question,
          allow_multiple: question.allow_multiple ?? question.multiple,
        }))
      : [],
    persistedAnswers: persistedAnswers.success ? persistedAnswers.data : null,
  };

  return questionContent;
}

function parseOrNull<ParsedValue>({
  schema,
  value,
}: {
  schema: z.ZodType<ParsedValue>;
  value: unknown;
}) {
  const parsedValue = schema.safeParse(value);
  return parsedValue.success ? parsedValue.data : null;
}

function extractStructuredToolOutput<ParsedValue>({
  output,
  schema,
}: {
  output: string | undefined;
  schema: z.ZodType<ParsedValue>;
}) {
  if (output === undefined) {
    return null;
  }

  const parsed = parseJsonOrNull(output);
  const directOutput = parseOrNull({ schema, value: parsed });
  if (directOutput) {
    return directOutput;
  }

  const content = Array.isArray(parsed)
    ? parsed
    : parseRecordOrNull(parsed)?.content;
  if (!Array.isArray(content)) {
    return null;
  }

  for (const contentItem of content) {
    const contentRecord = parseRecordOrNull(contentItem);
    if (
      contentRecord?.type === "text" &&
      typeof contentRecord.text === "string"
    ) {
      const contentOutput = parseOrNull({
        schema,
        value: parseJsonOrNull(contentRecord.text),
      });
      if (contentOutput) {
        return contentOutput;
      }
    }
  }

  return null;
}

function getIntegrationStatusContent({
  currentToolPart,
}: {
  currentToolPart: OpencodeToolPart;
}) {
  const integrationKey =
    typeof currentToolPart.state.input.integrationKey === "string"
      ? currentToolPart.state.input.integrationKey
      : null;
  const payload = extractStructuredToolOutput({
    output:
      currentToolPart.state.status === "completed"
        ? currentToolPart.state.output
        : undefined,
    schema: mcpShowIntegrationStatusOutputSchema,
  });
  const integrationStatusContent: AgentToolModel["content"] = {
    kind: "integrationStatus",
    integrationKey,
    payload: payload?.error === true ? null : payload,
  };

  return integrationStatusContent;
}

function getContentViewModel({
  currentSessionToolParts,
  currentToolPart,
}: {
  currentSessionToolParts: readonly OpencodeToolPart[];
  currentToolPart: OpencodeToolPart;
}) {
  const normalizedToolName = currentToolPart.tool.toLowerCase();
  let content: AgentToolModel["content"];

  if (normalizedToolName === "todowrite") {
    content = {
      kind: "todoWrite",
      state: getTodoWriteState({ currentSessionToolParts, currentToolPart }),
    };
  } else if (normalizedToolName === "question") {
    content = getQuestionContent({ currentToolPart });
  } else if (normalizedToolName === "task") {
    content = { kind: "subagentTask", callId: currentToolPart.callID };
  } else if (normalizedToolName === "suggest-followups") {
    const parsed =
      reploOpencodeHarnessSuggestFollowupsToolInputSchema.safeParse(
        currentToolPart.state.input,
      );
    content = {
      kind: "followUps",
      suggestions: parsed.success ? parsed.data.suggestions : [],
    };
  } else if (normalizedToolName.endsWith("show_integration_status")) {
    content = getIntegrationStatusContent({ currentToolPart });
  } else {
    content = { kind: "none" };
  }

  return content;
}

function getFallbackAgentToolModel({
  currentToolPart,
}: {
  currentToolPart: OpencodeToolPart;
}) {
  const summary = titleCase({ value: currentToolPart.tool.toLowerCase() });
  const fallbackAgentToolModel: AgentToolModel = {
    callId: currentToolPart.callID,
    content: { kind: "none" },
    debug: {
      input: {
        ...currentToolPart.state.input,
        tool: currentToolPart.tool,
      },
      output:
        currentToolPart.state.status === "completed"
          ? currentToolPart.state.output
          : null,
      error:
        currentToolPart.state.status === "error"
          ? currentToolPart.state.error
          : null,
    },
    display: {
      header:
        currentToolPart.state.status === "error"
          ? `${summary} failed`
          : summary,
      summary,
    },
    icon: getOpencodeToolIconKey({ toolName: currentToolPart.tool }),
    id: currentToolPart.id ?? currentToolPart.callID,
    integration: null,
    status: currentToolPart.state.status,
    timing: getTimingViewModel({ toolPart: currentToolPart }),
    toolName: currentToolPart.tool,
  };

  return fallbackAgentToolModel;
}

function getExecuteToolCalls({ toolPart }: { toolPart: OpencodeToolPart }) {
  const stateMetadata =
    "metadata" in toolPart.state ? toolPart.state.metadata : null;
  const parsed = opencodeExecuteToolMetadataSchema.safeParse(stateMetadata);
  return parsed.success ? parsed.data.toolCalls : [];
}

function getExecuteCallTime({ toolPart }: { toolPart: OpencodeToolPart }) {
  const stateTime = "time" in toolPart.state ? toolPart.state.time : null;
  const start = stateTime?.start ?? 0;
  const end =
    stateTime && "end" in stateTime && typeof stateTime.end === "number"
      ? stateTime.end
      : start;
  return { start, end };
}

// A call still "running" inside a settled part is stale metadata from an interrupted script.
function getExecuteCallStatus({
  call,
  toolPart,
}: {
  call: OpencodeExecuteToolCall;
  toolPart: OpencodeToolPart;
}) {
  const partStatus = toolPart.state.status;
  return call.status === "running" &&
    (partStatus === "completed" || partStatus === "error")
    ? partStatus
    : call.status;
}

function getExecuteCallState({
  call,
  output,
  toolPart,
}: {
  call: OpencodeExecuteToolCall;
  output: string;
  toolPart: OpencodeToolPart;
}): OpencodeToolState {
  const { start, end } = getExecuteCallTime({ toolPart });
  const input = call.input ?? {};
  const status = getExecuteCallStatus({ call, toolPart });
  return exhaustiveSwitch({ type: status })<OpencodeToolState>({
    running: () => ({ status: "running", input, time: { start } }),
    completed: () => ({
      status: "completed",
      input,
      output,
      title: "",
      metadata: {},
      time: { start, end },
    }),
    error: () => ({
      status: "error",
      input,
      error:
        toolPart.state.status === "error"
          ? toolPart.state.error
          : "Tool call failed",
      time: { start, end },
    }),
  });
}

// Code mode's built-in discovery namespace has no display registry entry.
function getCodemodeCallDisplay({ call }: { call: OpencodeExecuteToolCall }) {
  const summary =
    call.status === "completed"
      ? "Searched available tools"
      : "Searching available tools";
  const detail = quotedDetail({
    value: typeof call.input?.query === "string" ? call.input.query : null,
  });
  return { header: detail ? `${summary} ${detail}` : summary, summary };
}

/** Fans a code-mode execute part out to one row per MCP call the script made,
 * resolved through the same display pipeline as directly-called tools. */
export function getAgentToolModels({
  currentToolPart,
  messageParts,
  sessionToolParts,
}: {
  currentToolPart: OpencodeToolPart;
  messageParts: readonly OpencodeMessagePart[];
  sessionToolParts?: readonly OpencodeToolPart[];
}): AgentToolModel[] {
  const singleModel = () => [
    getAgentToolModel({ currentToolPart, messageParts, sessionToolParts }),
  ];
  if (currentToolPart.tool.toLowerCase() !== "execute") {
    return singleModel();
  }
  const calls = getExecuteToolCalls({ toolPart: currentToolPart });
  if (calls.length === 0) {
    return singleModel();
  }

  const baseId = currentToolPart.id ?? currentToolPart.callID;
  const scriptOutput =
    currentToolPart.state.status === "completed"
      ? currentToolPart.state.output
      : "";
  // The first row keeps the part's real ids so callId-keyed consumers
  // (phase headers, joins) still resolve; siblings get suffixed ids.
  return calls.map((call, callIndex) => {
    const callModel = getAgentToolModel({
      currentToolPart: {
        ...currentToolPart,
        id: callIndex === 0 ? currentToolPart.id : `${baseId}:${callIndex}`,
        callID:
          callIndex === 0
            ? currentToolPart.callID
            : `${currentToolPart.callID}:${callIndex}`,
        tool: call.tool,
        state: getExecuteCallState({
          call,
          output: calls.length === 1 ? scriptOutput : "",
          toolPart: currentToolPart,
        }),
      },
      messageParts,
      sessionToolParts,
    });
    const callModelWithOrigin = {
      ...callModel,
      partCallId: currentToolPart.callID,
    };
    return call.tool.startsWith("$codemode")
      ? { ...callModelWithOrigin, display: getCodemodeCallDisplay({ call }) }
      : callModelWithOrigin;
  });
}

export function getAgentToolModel({
  currentToolPart,
  messageParts,
  sessionToolParts,
}: {
  currentToolPart: OpencodeToolPart;
  messageParts: readonly OpencodeMessagePart[];
  sessionToolParts?: readonly OpencodeToolPart[];
}) {
  try {
    const currentSessionToolParts =
      sessionToolParts ??
      messageParts.flatMap((messagePart) => {
        return messagePart.partData.type === "tool"
          ? [messagePart.partData]
          : [];
      });
    const status = currentToolPart.state.status;
    const display = getRawToolDisplay({
      currentSessionToolParts,
      currentToolPart,
    });
    const content = getContentViewModel({
      currentSessionToolParts,
      currentToolPart,
    });
    const agentToolModel: AgentToolModel = {
      id: currentToolPart.id ?? currentToolPart.callID,
      callId: currentToolPart.callID,
      toolName: currentToolPart.tool,
      display: {
        header: display.header,
        summary: display.summary,
      },
      status,
      timing: getTimingViewModel({ toolPart: currentToolPart }),
      icon: getOpencodeToolIconKey({ toolName: currentToolPart.tool }),
      integration: getToolIntegrationRendering({ display }),
      debug: {
        input: {
          ...currentToolPart.state.input,
          tool: currentToolPart.tool,
        },
        output:
          currentToolPart.state.status === "completed"
            ? currentToolPart.state.output
            : null,
        error:
          currentToolPart.state.status === "error"
            ? currentToolPart.state.error
            : null,
      },
      content,
    };

    return agentToolModel;
  } catch {
    return getFallbackAgentToolModel({ currentToolPart });
  }
}
